<script>
// Components
import appConfig from "@/app.config.json";
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";

// Helpers
import UserUtils from '@/helpers/users';
import DateUtils from '@/helpers/date';
import FirestoreUtils from '@/helpers/firestore';
import {authComputed} from "@/state/helpers";

// Service
import ReviewsDataService from "@/services/ReviewsService";

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import isEmpty from "is-empty";


export default {
  name: "storeReviews",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Store Reviews",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    DatePicker,
  },
  computed: {
    ...authComputed
  },
  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      this.loadReviews();
    },

    handleDisplayChange() {
      this.loadReviews();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      } else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['store'] = this.filters.store.selected;
      params['appVersion'] = this.filters.appVersions.selected;
      if (params['appVersion'] !== 'all') {
        const [appVersion, store] = params['appVersion'].split('-');
        params['appVersion'] = appVersion;
        params['store'] = store;
      }
      params['osVersion'] = this.filters.osVersions.selected;
      if (params['osVersion'] !== 'all') {
        const [osVersion, store] = params['osVersion'].split('-');
        params['osVersion'] = osVersion;
        params['store'] = store;
      }
      params['device'] = this.filters.devices.selected;


      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    resetData() {
      this.stats = {
        reviews: '-',
        answered: '-',
        answered_perc: '-',
        rating_avg: '-',
      };
    },

    loadStats() {
      const params = this.getRequestParams();
      ReviewsDataService
          .getStoreStats(params)
          .then(response => {

            const {total, appVersionsGraph, osVersionsGraph, devicesGraph, historyGraph} = response.data;
            this.stats.reviews = total.reviews;
            this.stats.answered = total.answered;
            this.stats.answered_perc = total.answered_perc;
            this.stats.rating_avg = total.rating;

            this.appVersionsGraph.series = appVersionsGraph.series;
            this.appVersionsGraph.chartOptions = {
              ...this.appVersionsGraph.chartOptions, ...{
                labels: appVersionsGraph.labels,
                colors: appVersionsGraph.colors
              }
            };

            this.osVersionsGraph.series = osVersionsGraph.series;
            this.osVersionsGraph.chartOptions = {
              ...this.osVersionsGraph.chartOptions, ...{
                labels: osVersionsGraph.labels,
                colors: osVersionsGraph.colors
              }
            };

            this.devicesGraph.series = devicesGraph.series;
            this.devicesGraph.chartOptions = {
              ...this.devicesGraph.chartOptions, ...{
                labels: devicesGraph.labels,
                colors: devicesGraph.colors
              }
            };

            this.$refs.reviewsStoreRatingHistoryChart.updateSeries([{
              name: "Rating Score",
              type: 'bar',
              data: historyGraph.note
            }]);

            this.$refs.reviewsStoreHistoryChart.updateSeries([{
              name: "Reviews",
              type: 'bar',
              data: historyGraph.reviews
            }, {
              name: "Answered",
              type: 'area',
              data: historyGraph.answered
            }]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    loadReviews() {
      this.isLoading = true;
      const params = this.getRequestParams();

      ReviewsDataService
          .getStoreList(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.reviews = items;
            this.count = totalItems;
            this.isLoading = false;
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("reports.errorNoResult"));
            this.isLoading = false;
          });
    },

    getFilters() {
      ReviewsDataService.getStoreFilters()
          .then(response => {
            response.data.filters.appVersions.forEach(type => {
              this.filters.appVersions.options.push(type);
            });
            response.data.filters.osVersions.forEach(type => {
              this.filters.osVersions.options.push(type);
            });
            response.data.filters.devices.forEach(type => {
              this.filters.devices.options.push(type);
            });
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("reviews.getFiltersError"));
          });
    },

    processFilters() {
      this.resetData();
      this.loadStats();
      this.loadReviews();
    },

    isDeviceDataDisplayed(index) {
      return ["productName", "screenWidthPx", "screenHeightPx", "screenDensityDpi", "ramMb"].includes(index);
    },

    _isEmpty(val) {
      return isEmpty(val);
    },

    replyToReview(review, reviewText) {
      this.replyReview = review;
      this.replyObj.reviewText = reviewText;
      this.replyObj.author = review.author;
      this.$bvModal.show("review-reply-modal");
    },

    refreshReview(review) {
      const params = {
        id: review.id,
        reviewId: review.reviewId,
      };

      ReviewsDataService.refreshStoreReview(params)
          .then(response => {
            const {review} = response.data;
            const reviewObj = this.reviews.find(r => r.reviewId === review.reviewId);
            reviewObj.comments = review.comments;
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(error.message);
          });
    },

    sendReply() {
      const reply = this.replyObj.reply.trim();
      if (reply.length === 0) {
        this.$toast.error("Reply content is empty !");
        return;
      }

      if (reply.length < 20) {
        this.$toast.error("Reply content is too short ! (20 char. min)");
        return;
      }

      if (reply.length > 350) {
        this.$toast.error("Reply content is over 350 characters !");
        return;
      }

      if (isEmpty(this.replyReview.reviewId)) {
        this.$toast.error("Reply Obj ID is empty !");
        return;
      }

      this.isSendingReply = true;
      const params = {
        reviewId: this.replyReview.reviewId,
        id: this.replyReview.id,
        reply: reply,
        adminId: this.getUser.uid
      };

      ReviewsDataService.replyStoreReview(params)
          .then(response => {
            const { result } = response.data;
            if (result.success === false) {
              this.$toast.error("ERROR ! NE PAS RETENTER ET ATTENDRE MATHIEU !");
              this.$toast.error("ERROR ! NE PAS RETENTER ET ATTENDRE MATHIEU !!");
              this.$toast.error("ERROR ! NE PAS RETENTER ET ATTENDRE MATHIEU !!!");
              return;
            }

            const reviewObj = this.reviews.find(r => r.id === result.id);
            if (!isEmpty(reviewObj)) {
              this.stats.answered++;
              reviewObj.answered = 1;
              reviewObj.adminName = result.adminName;
              reviewObj.comments = result.comments;
            }

            this.replyObj.reviewText = '';
            this.replyObj.author = '';
            this.replyObj.reply = '';
            this.$bvModal.hide("review-reply-modal");
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(error.message);
          })
          .finally(() => {
            this.isSendingReply = false;
          });
    },

    generateReply() {
      this.isSendingReply = true;
      const params = {
        id: this.replyReview.id,
        instructions: this.replyObj.instructions,
        content: this.replyObj.reviewText,
      };

      ReviewsDataService.generateReplyStoreReview(params)
          .then(response => {
            const {result} = response.data;
            if (result.reviewId === this.replyReview.id) {
              if (!isEmpty(result.message)) {
                this.replyObj.reply = result.message;
              }
              else {
                this.$toast.warning("Aucun message généré !");
              }
            }
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(error.message);
          })
          .finally(() => {
            this.isSendingReply = false;
          });
    },

    cancelReply() {
      this.replyReview = { };
      this.replyObj.reviewText = '';
      this.replyObj.author = '';
      this.replyObj.reply = '';
      this.$bvModal.hide("review-reply-modal");
    },

    async loadInstructions() {
      this.replyObj.instructions = await FirestoreUtils.loadReviewsInstructions();
      if (isEmpty(this.replyObj.instructions)) {
        this.$toast.error("Reply Instructions is empty !");
      }
    },

    saveInstructions() {
      if (isEmpty(this.replyObj.instructions)) {
        this.$toast.error("Empty instructions !");
        return;
      }

      if (this.isSendingReply) {
        return;
      }

      this.isSendingReply = true;
      FirestoreUtils.saveReviewsInstructions(this.replyObj.instructions)
        .then(() => {
          this.$toast.success("Instructions saved successfully!");
        })
        .catch(error => {
          window.console.error(error);
          this.$toast.error(error.message);
        })
        .finally(() => {
          this.isSendingReply = false;
      });
    }

  },
  mounted() {
    this.loadInstructions();
    this.getFilters();
    this.processFilters();
  },
  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.support.store_reviews"),
      reviews: [],
      replyReview: { },
      isSendingReply: false,
      replyObj: {
        author: '',
        reviewText: '',
        reply: '',
        instructions: '',
      },
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '50'
      },
      stats: {
        reviews: '-',
        answered: '-',
        answered_perc: '-',
        rating_avg: '-',
      },

      appVersionsGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'store-reviews-appVersions',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      osVersionsGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'store-reviews-osVersions',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      devicesGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'store-reviews-devices',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },

      historyNoteGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: false,
            type: 'bar',
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          dataLabels: {
            enabled: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          legend: {
            position: "bottom",
          },
          yaxis: [{
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
              color: '#666666'
            },
            labels: {
              style: {
                colors: '#666666',
              }
            },
            title: {
              text: "Note",
              style: {
                color: '#666666',
              }
            },
            tooltip: {
              enabled: true
            },
            min: 0.0,
            max: 6.0
          }]
        }
      },
      historyGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: false,
            type: 'line',
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
              color: '#666666'
            },
            labels: {
              style: {
                colors: '#666666',
              }
            },
            title: {
              text: "Status",
              style: {
                color: '#666666',
              }
            },
            tooltip: {
              enabled: true
            }
          }],
          colors: ["#095bc5", "#34c38f"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },

      filters: {
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'last30Day',
          options: [
            {value:'week', text:this.$t("dates.week")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        store: {
          selected: 'all',
          options: [
            {value: 'all', text: "All Store"},
            {value: 'GP', text: "Google PlayStore"},
            {value: 'AS', text: "Apple AppStore"},
          ]
        },
        appVersions: {
          selected: 'all',
          options: [
            {value: 'all', text: "All App versions"}
          ]
        },
        osVersions: {
          selected: 'all',
          options: [
            {value: 'all', text: "All OS versions"}
          ]
        },
        devices: {
          selected: 'all',
          options: [
            {value: 'all', text: "All Devices Brand"}
          ]
        },
      },

    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">

      <!-- Filters -->
      <div class="card">
        <div class="card-body row">
          <b-form inline class="row gy-2 gx-3 align-items-center" id="reportsFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="reportsDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>
            </div>

            <!-- Store -->
            <div class="col-sm-auto">
              <b-form-select
                  v-model="filters.store.selected"
                  class="form-select me-lg-1"
                  :options="filters.store.options">
              </b-form-select>
            </div>

            <!-- AppVersions -->
            <div class="col-sm-auto">
              <b-form-select
                  v-model="filters.appVersions.selected"
                  class="form-select me-lg-1"
                  :options="filters.appVersions.options">
              </b-form-select>
            </div>

            <!-- OsVersions -->
            <div class="col-sm-auto">
              <b-form-select
                  v-model="filters.osVersions.selected"
                  class="form-select me-lg-1"
                  :options="filters.osVersions.options">
              </b-form-select>
            </div>

            <!-- Devices -->
            <div class="col-sm-auto">
              <b-form-select
                  v-model="filters.devices.selected"
                  class="form-select me-lg-1"
                  :options="filters.devices.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row">

        <div class="col-12 col-lg-4">
          <!-- Note -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">Reviews Added</p>
                  <h4 class="mb-0">{{ stats.reviews }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                <span class="avatar-title bg-info">
                  <i class="bx bx-archive-in font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <!-- Displayed -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">Reviews Replied</p>
                  <h4 class="mb-0">{{ stats.answered }} <small>{{ stats.answered_perc }}%</small></h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                <span class="avatar-title bg-success">
                  <i class="bx bx-archive-in font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <!-- Rating -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-muted fw-medium mb-2">AVG Rating</p>
                  <h4 class="mb-0">{{ stats.rating_avg }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                <span class="avatar-title bg-pink">
                  <i class="bx bx-error font-size-24"></i>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Graphs -->
      <div class="row">

        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">App Versions</h5>
              <apexchart
                  ref="reviewsAppVersionsChart"
                  height="350"
                  type="donut"
                  dir="ltr"
                  :series="appVersionsGraph.series"
                  :options="appVersionsGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">OS Versions</h5>
              <apexchart
                  ref="reviewsOsVersionsChart"
                  height="350"
                  type="donut"
                  dir="ltr"
                  :series="osVersionsGraph.series"
                  :options="osVersionsGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Devices</h5>
              <apexchart
                  ref="reviewsDevicesChart"
                  height="350"
                  type="donut"
                  dir="ltr"
                  :series="devicesGraph.series"
                  :options="devicesGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- History Graphs -->
      <div class="row">

        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Rating History</h5>
              <apexchart
                  ref="reviewsStoreRatingHistoryChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="300"
                  :series="historyNoteGraph.series"
                  :options="historyNoteGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">Reviews History</h5>
              <apexchart
                  ref="reviewsStoreHistoryChart"
                  class="apex-charts"
                  type="line"
                  dir="ltr"
                  height="300"
                  :series="historyGraph.series"
                  :options="historyGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="reviews-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row" v-if="!isLoading && reviews.length > 0">

        <div class="card mt-2" v-for="review in reviews" :key="review.reviewId">
          <div class="card-body">
            <div class="p-2 bg-light mb-2 rounded-3">
              <span class="text-muted font-size-13 me-2">{{ formatDateString(review.createdAt, 'DD MMM YYYY HH:mm') }}</span>
              <span class="text-primary font-size-13 fw-bold me-1">{{ review.author}} </span>
              <span class="badge bg-dark font-size-13 text-warning me-2">{{ review.rating }} ⭐</span>
              <span class="badge bg-info font-size-13 text-light me-2">v{{ review.appVersion }}</span>
              <span
                  class="badge font-size-13 me-2"
                  :class="{
                          'bg-success text-light': `${review.store}` === 'GP',
                          'bg-pink text-like': `${review.store}` === 'AP',
                        }"
              >{{ $t(`reviews.store_${review.store}`) }}</span>
              <span class="badge bg-light text-dark font-size-13">{{ review.device}} OS {{ review.osVersion }}</span>
              <span v-if="review.answered === 1" class="badge bg-warning text-dark float-end font-size-13">Replied</span>
            </div>

            <div v-for="(comment, index) in JSON.parse(review.comments)" :key="index">

              <div v-if="!_isEmpty(comment.userComment)">

                <span v-for="(deviceData, index) in comment.userComment.deviceMetadata" :key="index">
                  <span v-if="isDeviceDataDisplayed(index)" class="me-2 font-size-12">{{ index }}: <b>{{ deviceData }}</b></span>
                </span>

                <div
                    style="width:80%;"
                    class="mt-3 rounded-4 mb-3 float-start msg-user font-size-13"
                    >
                  {{ comment.userComment.text }}
                  <p v-if="!_isEmpty(comment.userComment.originalText)">
                    <small class="fst-italic text-muted">{{ comment.userComment.originalText }}<br>Language: <strong>{{ comment.userComment.reviewerLanguage}}</strong></small>
                  </p>
                  <hr>
                  <b-button variant="primary" class="w-md" @click="replyToReview(review, comment.userComment.text)">Reply</b-button>
                 <!-- <b-button variant="secondary" size="sm" class="w-md ms-2" @click="refreshReview(review)">refresh</b-button>-->
                </div>
              </div>

              <div v-if="!_isEmpty(comment.developerComment)">
                <div
                    style="width:80%;"
                    class="rounded-4 mb-3 float-end msg-sender"
                    >
                  <p class="text-black fw-bold font-size-12">
                      {{ formatDateTimestamp(comment.developerComment.lastModified.seconds, 'DD MMM YYYY HH:mm') }}
                      <span v-if="!_isEmpty(review.adminName)" class="fst-italic">&nbsp;by {{ review.adminName }}</span>
                  </p>
                  <p class="font-size-13">{{ comment.developerComment.text }}</p>
                </div>
              </div>
            </div>

          </div>
        </div>


      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && reviews.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No reviews were found for these filters. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
      <!-- Display settings -->
      <div class="col-lg-auto col-12">
        <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
          <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
        </b-select>
      </div>
      <!-- Pagination -->
      <div class="col-lg-auto col-12">
        <div class="m-1 pagination-rounded">
          <b-pagination
              v-model="page"
              :total-rows="count"
              :per-page="pageSizes.selected"
              first-number
              last-number
              @change="handlePageChange"
              aria-controls="reviews-list"
          ></b-pagination>
        </div>
      </div>
    </div>

    </div>

    <b-modal id="review-reply-modal" size="xl" hide-footer>
      <template #modal-title>
        Reply to {{ replyObj.author }}
      </template>
      <div>

        <div class="font-size-12 bg-light fst-italic p-2">
          <span class="text-primary font-size-14 fw-bold">&lt;&lt;</span>{{ replyObj.reviewText}}<span class="text-primary font-size-14 fw-bold">&gt;&gt;</span>
        </div>

        <b-textarea
          id="replyInstructions"
          v-model="replyObj.instructions"
          rows="6"
          class="bg-white mb-2 mt-2 font-size-12"
          placeholder="Type instructions…"
        >

        </b-textarea>

        <b-button v-show="!_isEmpty(replyObj.instructions) && !isSendingReply" size="sm" class="ms-2 float-end mt-1 mb-2" variant="success" @click="saveInstructions()">
          Save Prompt
        </b-button>

        <b-form-textarea
            id="replyContent"
            v-model="replyObj.reply"
            rows="6"
            class="bg-white text-black font-size-14"
            placeholder="Type reply…"
            :state="replyObj.reply.length >= 20 && replyObj.reply.length <= 350"
        >
        </b-form-textarea>
        <br>

        <div class="row d-flex justify-content-center">
          <vue-ellipse-progress
              v-show="isSendingReply"
              :loading="isSendingReply"
              :angle="-90"
              color="#50a5f1"
              emptyColor="#999"
              emptyColorFill="#f8f8fb"
              :size="50"
              :thickness="3"
              :emptyThickness="2"
              lineMode="out 2"
              animation="bounce 700 400"
              fontColor="#666"
              :half="false"
              class="mt-5 mb-5"
              fontSize="1rem">
          </vue-ellipse-progress>
        </div>

        <b-button v-show="!_isEmpty(replyObj.reply) && !isSendingReply" class="ms-2" variant="success" @click="sendReply()">
          Send Reply
        </b-button>
        <b-button v-show="!isSendingReply" class="ms-2" variant="primary" @click="generateReply()">
          Generate Reply
        </b-button>
        <b-button v-show="!isSendingReply" class="ms-2" variant="danger" @click="cancelReply()">
          Cancel Reply
        </b-button>
      </div>
    </b-modal>

  </Layout>
</template>

<style>

.msg-sender {
  background-color: #9be2ef;
  padding:10px;
}

.msg-user {
  background-color: #f2f2f2;
  padding:10px;
}

.cursor-pointer {
  cursor: pointer;
}

.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}
</style>
